import { formatDateTime } from '@root/utils/helpers/formatDate';
import { Button, Col, Form, Input, Radio, Row, Tabs } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import saveAs from 'file-saver';
import { useEffect, useState } from 'react';
import {
  Attachment,
  Comment,
  CommentForm,
  CommentGroup,
  CommentProps,
  CommentSide,
  CommentStatus,
  CommentTabs,
  CommentType,
  CreateComment
} from './interface';
import './_index.scss';
import commentService from './commentService';
import { CURRENT_ENV } from '@root/core/configs/env';
import { useHistory } from 'react-router';
import Popup from '../Popup';
import { CommentLang } from './comment.lang';

const CommentTabsComponent = ({
  style,
  tabs,
  onSend,
  commentList,
  title,
  refectComments,
  itemId,
  commentGroup,
  updateItem,
  removeItem,
  removeTitle,
  disabled,
  showAttachmentsDownload,
  isWarehouseSendCommentFirst,
  inspectionLabel
}: CommentProps) => {
  const [form] = Form.useForm();
  const [files, setFiles] = useState<File[]>([]);
  const [currentTab, setCurrentTab] = useState<CommentTabs>(tabs?.[0]);
  const [activeKey, setActiveKey] = useState('0');
  const [sendDisable, setSendDisable] = useState(false);
  const [tabDisable, setTabDisable] = useState(false);
  const [openPopupDelete, setOpenPopupDelete] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentEditValue, setCommentEditValue] = useState<string>();
  const [commentEditFile, setCommentEditFile] = useState<File[]>([]);
  const history = useHistory();
  const [historyComment, setHistoryComment] = useState({} as any);
  const lang = CommentLang.get;

  const currentSide =
    CURRENT_ENV.APP_ROLE === 'seller-admin'
      ? CommentSide.SELLER
      : CommentSide.WAREHOUSE;

  useEffect(() => {
    checkCanSendComment();
    if (disabled) {
      setSendDisable(disabled);
    }

    setComments(commentList?.data ?? []);
  }, [commentList]);

  useEffect(() => {
    setCurrentTab(tabs?.[0]);
  }, [tabs]);

  const checkCanSendComment = (type: CommentType = currentTab?.type) => {
    if (type) {
      if (!disabled) {
        const results = (commentList?.data ?? []).filter(
          (c) => c.type === type
        );

        if (
          results.length &&
          results[results.length - 1].side === currentSide
        ) {
          if (
            type === CommentType.REQUEST_DELETE ||
            type === CommentType.REQUEST_MODIFY
          ) {
            if (results[results.length - 1].status === CommentStatus.REQUEST) {
              setSendDisable(true);
            } else {
              setSendDisable(false);
            }
          } else {
            setSendDisable(true);
          }
        } else if (
          isWarehouseSendCommentFirst &&
          type === CommentType.COMMON &&
          results.length === 0
        ) {
          setSendDisable(true);
        } else {
          setSendDisable(false);
        }
      } else {
        setSendDisable(true);
      }
    }
  };

  useEffect(() => {
    checkCanSendComment();
  }, [disabled, tabs, currentTab]);

  const checkCanEditComment = (comment: Comment) => {
    if (!tabs || !tabs.length) return false;
    if (comment.side !== currentSide) return false;

    const result = (commentList?.data ?? []).filter(
      (c) => c.type === CommentType.COMMON
    );

    if (result.length && result[result.length - 1].id === comment.id) {
      return true;
    }
    return false;
  };

  const handleFileChange = (e: any) => {
    const target = e.target as HTMLInputElement;

    if (target.files && target.files.length) {
      const fileList = [...files, target.files[0]];
      setFiles(fileList);
      form.setFieldsValue({
        files: fileList
      });
    }
    // reset
    target.value = '';
  };

  const handleFileDelete = (imageIndex: number) => {
    const fileList = files.filter((_, idx) => idx !== imageIndex);
    setFiles(fileList);
    form.setFieldsValue({
      files: fileList
    });
  };

  const handleFileDownload = (attachments: Attachment[]) => {
    (attachments ?? []).map((attachment) => {
      if (attachment.id) {
        saveAs(attachment.url, attachment.fileName);
      }
    });
  };

  const handleRequestToDeleteComment = async (comment: Comment) => {
    await commentService.requestDeleteComment(comment.id);
    if (refectComments) {
      await refectComments(true);
    }
  };

  const handleDeleteComment = async (comment: Comment) => {
    await commentService.deleteComment(comment.id);
    if (refectComments) {
      await refectComments(true);
    }
  };

  const setCanUpdate = async (comment: Comment) => {
    setComments(
      comments.map((c) => {
        if (c.id === comment.id) {
          return {
            ...c,
            disabled: false
          };
        }
        return c;
      })
    );
  };
  const handleUpdate = async (comment: Comment) => {
    setComments(
      comments.map((c) => {
        if (c.id === comment.id) {
          return {
            ...c,
            disabled: true
          };
        }
        return c;
      })
    );

    let attachmentIds: number[] = [];
    if (commentEditFile.length) {
      const fileResult = await commentService.commentUpload({
        attachments: commentEditFile
      });
      attachmentIds = (fileResult?.data?.attachments ?? []).map(
        (f: any) => f.id
      );
    }

    await commentService.updateComment(comment.id, {
      comment:
        commentEditValue !== undefined ? commentEditValue : comment.comment,
      attachmentIds: [
        ...comment.attachments.filter((a) => a.id !== 0).map((a) => a.id),
        ...attachmentIds
      ].join(',')
    });
  };

  const handleApproved = async (comment: Comment) => {
    await commentService.approvedRequest(comment.id);
    if (refectComments) {
      await refectComments(true);
    }
  };

  const handleRejected = async (comment: Comment) => {
    await commentService.rejectedRequest(comment.id);
    if (refectComments) {
      await refectComments(true);
    }
  };

  const apceptReturn = async (id: number) => {
    await commentService.apceptReturn(id);
  };

  /**
   * Send Comment
   */
  const onFinish = (data: CommentForm) => {
    const createComment: CreateComment = {
      comment: data.comment,
      group: commentGroup,
      type: currentTab.type,
      itemId
    };
    switch (currentTab.type) {
      case CommentType.INSPECTION:
        // body.extraData = data.files;
        break;
      case CommentType.REQUEST_MODIFY:
      case CommentType.REQUEST_DELETE:
        createComment.extraData = data.reason;
        break;
    }

    if (currentTab.imageUpload) {
      createComment.files = data.files;
    }

    onSend(createComment, data.id);

    // reset to default
    setSendDisable(true);
    setTabDisable(false);
    form.resetFields();
    setFiles([]);
    setHistoryComment({});
  };

  return (
    <div className="comment-wrapper" style={style}>
      <Form
        form={form}
        name="basic"
        // initialValues={}
        onFinish={onFinish}
        layout="vertical"
        onChange={(e) => {
          const target = e.target as HTMLInputElement;
          if (target.type === 'textarea') {
            if (target.id) {
              setHistoryComment({
                ...historyComment,
                [currentTab.type]: target.value
              });
            }
          }
          if (target.type === 'radio') {
            const data = form.getFieldsValue();
            form.setFieldsValue({
              ...data,
              reason: target.value
            });
          }
        }}
      >
        <p>{title}</p>
        {comments.map((comment, cIdx) => {
          let commentFrom = '';
          switch (comment.side) {
            case CommentSide.SELLER:
              commentFrom = 'Comment from Customer :';
              if (comment.type === CommentType.REQUEST_MODIFY) {
                commentFrom = 'Reasons for modification from Customer :';
              } else if (comment.type === CommentType.REQUEST_DELETE) {
                commentFrom = 'Reason for deletion from Customer :';
              }
              break;
            case CommentSide.WAREHOUSE:
              commentFrom = 'Comment from Warehouse :';
              if (comment.type === CommentType.REQUEST_MODIFY) {
                commentFrom = 'Reasons for modification from Warehouse :';
              } else if (comment.type === CommentType.REQUEST_DELETE) {
                commentFrom = 'Reason for deletion from Warehouse :';
              }
              break;
          }

          let row = null;
          switch (comment.type) {
            case CommentType.INSPECTION:
              row = (
                <Row className="warehouse-inspection" key={cIdx}>
                  <Col span={24}>
                    <p>
                      {inspectionLabel ? inspectionLabel : '물류센터 검수 내용'}
                    </p>
                  </Col>
                  <Col span={12}>
                    <Row>
                      {(comment?.attachments ?? []).map((attachment, iIdx) => (
                        <Col span={8} key={iIdx}>
                          <div className="comment_image">
                            <img alt="" src={attachment.url} />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row className="comment_content">
                      <Col span={12}>{commentFrom}</Col>
                      <Col span={12} className="comment_date">
                        {formatDateTime(comment.createdAt)}
                      </Col>
                      <Col span={24} style={{ paddingTop: '10px' }}>
                        <TextArea rows={4} disabled value={comment.comment} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
              break;
            case CommentType.REQUEST_MODIFY:
            case CommentType.REQUEST_DELETE:
            case CommentType.COMMON:
              const isCommonComment = comment.type === CommentType.COMMON;
              let span = isCommonComment ? 24 : 20;
              const currentSide =
                CURRENT_ENV.APP_ROLE === 'seller-admin'
                  ? CommentSide.SELLER
                  : CommentSide.WAREHOUSE;

              let btn;
              if (!isCommonComment) {
                switch (comment.status) {
                  case CommentStatus.REQUEST:
                    if (currentSide !== comment.side) {
                      btn = (
                        <Col span={4} className="align-end">
                          <div>
                            <Button
                              style={{ marginRight: '10px' }}
                              onClick={() => handleRejected(comment)}
                            >
                              {comment.type === CommentType.REQUEST_MODIFY
                                ? lang('rejectModify')
                                : lang('rejectDelete')}
                            </Button>
                            <Button
                              className="btn-submit"
                              onClick={() => handleApproved(comment)}
                            >
                              {comment.type === CommentType.REQUEST_MODIFY
                                ? lang('modifyApproval')
                                : lang('deleteApproval')}
                            </Button>
                          </div>
                        </Col>
                      );

                      if (comment.group === CommentGroup.RETURN_DETAIL) {
                        if (comment.extraData === '1') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>
                                <Button
                                  className="btn-submit"
y                                  onClick={() => {
                                    handleApproved(comment)
                                    apceptReturn(itemId)
                                  }}
                                >
                                  {lang('confirmReturn')}
                                </Button>
                              </div>
                            </Col>
                          );
                        }

                        if (comment.extraData === '2') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>
                                <Button
                                  className="btn-submit"
                                  onClick={() => handleApproved(comment)}
                                >
                                  {lang('confirmDisposal')}
                                </Button>
                              </div>
                            </Col>
                          );
                        }

                        if (comment.extraData === '3') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>
                                <Button
                                  className="btn-submit"
                                  onClick={() => handleApproved(comment)}
                                >
                                  {lang('inventoryApproved')}
                                </Button>
                              </div>
                            </Col>
                          );
                        }
                      }
                    } else {
                      btn = (
                        <Col span={4} className="align-end">
                          <div>
                            <Button disabled={true}>
                              {lang('requesting')}
                            </Button>
                          </div>
                        </Col>
                      );
                    }
                    break;
                  case CommentStatus.APPROVE:
                    btn = (
                      <Col span={4} className="align-end">
                        <div>
                          {comment.type === CommentType.REQUEST_MODIFY
                            ? lang('modifyApproval')
                            : lang('deleteApproval')}
                        </div>
                      </Col>
                    );
                    if (currentSide === comment.side) {
                      switch (commentGroup) {
                        case CommentGroup.DELIVERY_DETAIL:
                          if (comment.type === CommentType.REQUEST_DELETE) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      setOpenPopupDelete(true);
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('delete')}
                                  </Button>
                                </div>
                              </Col>
                            );
                          } else if (
                            comment.type === CommentType.REQUEST_MODIFY
                          ) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      if (currentSide === CommentSide.SELLER) {
                                        history.push(
                                          `/admin/seller/shipping/edit/${itemId}/${comment.id}`
                                        );
                                      }
                                      if (
                                        currentSide === CommentSide.WAREHOUSE
                                      ) {
                                        history.push(
                                          `/admin/delivery/edit/${itemId}/${comment.id}`
                                        );
                                      }
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('edit')}
                                  </Button>
                                </div>
                              </Col>
                            );
                          }
                          break;
                        case CommentGroup.INBOUND_DETAIL:
                          if (comment.type === CommentType.REQUEST_DELETE) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      setOpenPopupDelete(true);
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('delete')}
                                  </Button>
                                </div>
                              </Col>
                            );
                          } else if (
                            comment.type === CommentType.REQUEST_MODIFY
                          ) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      if (currentSide === CommentSide.SELLER) {
                                        updateItem?.(comment.id);
                                      }
                                      if (
                                        currentSide === CommentSide.WAREHOUSE
                                      ) {
                                        updateItem?.(comment.id);
                                      }
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('edit')}
                                  </Button>
                                </div>
                              </Col>
                            );
                          }
                          break;
                        case CommentGroup.RETURN_DETAIL:
                          if (comment.type === CommentType.REQUEST_DELETE) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      setOpenPopupDelete(true);
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('delete')}
                                  </Button>
                                </div>
                              </Col>
                            );
                          } else if (
                            comment.type === CommentType.REQUEST_MODIFY
                          ) {
                            btn = (
                              <Col span={4} className="align-end">
                                <div>
                                  <Button
                                    onClick={() => {
                                      if (currentSide === CommentSide.SELLER) {
                                        updateItem?.(comment.id);
                                      }
                                      if (
                                        currentSide === CommentSide.WAREHOUSE
                                      ) {
                                        updateItem?.(comment.id);
                                      }
                                    }}
                                    className="btn-submit"
                                  >
                                    {lang('edit')}
                                  </Button>
                                </div>
                              </Col>
                            );
                            if (comment.extraData === '1') {
                              btn = (
                                <Col span={4} className="align-end">
                                  <div>{lang('returnApproved')}</div>
                                </Col>
                              );
                            }
                            if (comment.extraData === '2') {
                              btn = (
                                <Col span={4} className="align-end">
                                  <div>{lang('disposalApproved')}</div>
                                </Col>
                              );
                            }
                            if (comment.extraData === '3') {
                              btn = (
                                <Col span={4} className="align-end">
                                  <div>{lang('inventoryApproved')}</div>
                                </Col>
                              );
                            }
                          }
                          break;
                      }
                    } else {
                      if (comment.group === CommentGroup.RETURN_DETAIL) {
                        if (comment.extraData === '1') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>{lang('returnApproved')}</div>
                            </Col>
                          );
                        }
                        if (comment.extraData === '2') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>{lang('disposalApproved')}</div>
                            </Col>
                          );
                        }
                        if (comment.extraData === '3') {
                          btn = (
                            <Col span={4} className="align-end">
                              <div>{lang('inventoryApproved')}</div>
                            </Col>
                          );
                        }
                      }
                    }
                    break;
                  case CommentStatus.REJECT:
                    let rejectText = '';
                    switch (comment.type) {
                      case CommentType.REQUEST_MODIFY:
                        if (comment.side === currentSide) {
                          rejectText = lang('modifyRejected');
                        } else {
                          rejectText = lang('rejectedModify');
                        }
                        break;
                      case CommentType.REQUEST_DELETE:
                        if (comment.side === currentSide) {
                          rejectText = lang('deleteRejected');
                        } else {
                          rejectText = lang('rejectedDelete');
                        }
                        break;
                    }
                    btn = (
                      <Col span={4} className="align-end">
                        <div>{rejectText}</div>
                      </Col>
                    );
                    break;
                  case CommentStatus.EDITED:
                    btn = (
                      <Col span={4} className="align-end">
                        <div>{lang('modified')}</div>
                      </Col>
                    );
                    break;
                }
              } else if (checkCanEditComment(comment)) {
                span = 20;
                btn = (
                  <Col span={4} className="align-end">
                    <div>
                      <Button
                        onClick={() => {
                          if (comment.disabled === false) {
                            handleUpdate(comment);
                          } else {
                            setCanUpdate(comment);
                          }
                        }}
                        className={
                          comment.disabled === false ? 'btn-submit' : ''
                        }
                      >
                        {comment.disabled === false
                          ? lang('send')
                          : lang('adjust')}
                      </Button>
                    </div>
                  </Col>
                );
              } else if (
                !checkCanEditComment(comment) &&
                comment.group === CommentGroup.INQUIRY_DETAIL &&
                comment.side === CommentSide.WAREHOUSE
              ) {
                if (currentSide === CommentSide.WAREHOUSE) {
                  span = 20;
                  btn = (
                    <Col span={4} className="align-end">
                      <div>
                        <Button
                          onClick={() => {
                            handleRequestToDeleteComment(comment);
                          }}
                          disabled={
                            comment.status === CommentStatus.REQUEST_DELETED
                          }
                          className={
                            comment.status === CommentStatus.REQUEST
                              ? 'btn-submit'
                              : ''
                          }
                        >
                          {comment.status === CommentStatus.REQUEST
                            ? lang('requestDelete')
                            : lang('requesting')}
                        </Button>
                      </div>
                    </Col>
                  );
                } else if (comment.status === CommentStatus.REQUEST_DELETED) {
                  span = 20;
                  btn = (
                    <Col span={4} className="align-end">
                      <div>
                        <Button
                          onClick={() => {
                            handleDeleteComment(comment);
                          }}
                          className={'btn-submit'}
                        >
                          {lang('deleteApproval')}
                        </Button>
                      </div>
                    </Col>
                  );
                }
              }

              if (
                (comment.attachments &&
                  comment.attachments.length &&
                  Array.isArray(comment.attachments)) ||
                showAttachmentsDownload
              ) {
                span = 13;
                row = (
                  <Row className="comment-reason">
                    <Col span={4}>
                      {comment.side === 'SELLER'
                        ? 'Customer’s attachments'
                        : 'Warehouse’s attachments'}
                    </Col>
                    <Col
                      span={3}
                      style={{ textAlign: 'right', paddingRight: '10px' }}
                    >
                      {comment.disabled === false ? (
                        <label
                          className="ant-btn ant-btn-default"
                          style={{ borderRadius: '8px' }}
                        >
                          <input
                            onChange={(e) => {
                              const target = e.target;
                              if (target.files && target.files.length) {
                                setCommentEditFile([
                                  ...commentEditFile,
                                  target.files[0]
                                ]);
                                comment.attachments.push({
                                  id: 0,
                                  url: '',
                                  fileName: target.files[0].name
                                });
                              }
                              // reset
                              target.value = '';
                            }}
                            type="file"
                            style={{ display: 'none' }}
                            accept="image/*"
                          ></input>
                          Upload
                        </label>
                      ) : (
                        <Button
                          className={'btn-submit'}
                          onClick={() =>
                            handleFileDownload(comment.attachments)
                          }
                        >
                          Download
                        </Button>
                      )}
                    </Col>
                    <Col span={span}>{commentFrom}</Col>
                    <Col span={4} className="comment_date">
                      {formatDateTime(comment.createdAt)}
                    </Col>
                    <Col span={7}>
                      {comment.attachments.map((attachment, idx) => (
                        <div className="pre-image-name">
                          {attachment.fileName}
                          {comment.disabled === false ? (
                            <span
                              style={{ color: 'black' }}
                              className="pre-image-icon-del"
                              onClick={() => {
                                setCommentEditFile([...commentEditFile]);
                                comment.attachments =
                                  comment.attachments.filter(
                                    (_, index) => index !== idx
                                  );
                              }}
                            >
                              X
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </Col>
                    <Col
                      span={btn === undefined ? span + 4 : span}
                      style={{ paddingTop: '10px' }}
                    >
                      <TextArea
                        rows={4}
                        defaultValue={comment.comment}
                        onChange={(e: any) => {
                          setCommentEditValue(e.target.value);
                        }}
                        disabled={comment.disabled ?? true}
                      />
                    </Col>
                    {btn}
                  </Row>
                );
              } else {
                row = (
                  <Row className="comment-reason">
                    <Col span={12}>{commentFrom}</Col>
                    <Col span={12} className="comment_date">
                      {formatDateTime(comment.createdAt)}
                    </Col>
                    <Col span={span} style={{ paddingTop: '10px' }}>
                      <TextArea
                        rows={4}
                        defaultValue={comment.comment}
                        onChange={(e: any) => {
                          setCommentEditValue(e.target.value);
                        }}
                        disabled={comment.disabled ?? true}
                      />
                    </Col>
                    {btn}
                  </Row>
                );
              }
              break;
          }

          return row;
        })}

        {tabs.length ? (
          <Row className="comment_sending">
            <Tabs
              activeKey={activeKey}
              onChange={(tabIndex) => {
                const tab = tabs[+tabIndex];
                setCurrentTab(tab);
                form.setFieldsValue({
                  comment: historyComment[tab.type]
                });
                checkCanSendComment(tab.type);
              }}
              onTabClick={(tabIndex) => {
                if (!tabDisable) {
                  setActiveKey(tabIndex);
                }
              }}
            >
              {tabs.map((tab, idx) => {
                let commentSpan = 20;
                commentSpan =
                  tab.options && tab.options.length ? 17 : commentSpan;
                commentSpan = tab.imageUpload ? 13 : commentSpan;

                return (
                  <Tabs.TabPane tab={tab.title} key={idx}>
                    <Row className="comment-reason_wrapper">
                      {tab.description && !tab.imageUpload ? (
                        <Col span={24}>
                          <p>{tab.description}</p>
                        </Col>
                      ) : null}

                      {tab.options && tab.options.length ? (
                        <Col span={3}>
                          <Form.Item name="reason">
                            {' '}
                            <Radio.Group
                              options={tab.options}
                              disabled={sendDisable}
                            />
                          </Form.Item>
                        </Col>
                      ) : null}

                      {tab.imageUpload ? (
                        <Col
                          span={7}
                          style={{
                            padding: '5px',
                            borderRight: '1px solid #d9d9d9'
                          }}
                        >
                          <Row>
                            <Col span={18}>{tab.imageDescription}</Col>
                            <Col span={4}>
                              <label
                                className="ant-btn ant-btn-default"
                                style={{ borderRadius: '8px' }}
                              >
                                <input
                                  disabled={sendDisable}
                                  onChange={handleFileChange}
                                  type="file"
                                  style={{ display: 'none' }}
                                  accept="image/*"
                                ></input>
                                Upload
                              </label>
                            </Col>
                          </Row>
                          <Form.Item name="files">
                            {files.map((file, idx) => (
                              <Row key={idx}>
                                <Col className="pre-image-name">
                                  {file.name}
                                </Col>
                                <Col
                                  onClick={() => handleFileDelete(idx)}
                                  className="pre-image-icon-del"
                                >
                                  X
                                </Col>
                              </Row>
                            ))}
                          </Form.Item>
                        </Col>
                      ) : null}

                      <Col
                        span={commentSpan}
                        style={{
                          paddingLeft: tab.imageUpload ? '10px' : 0
                        }}
                      >
                        {tab.commentDescription ? (
                          <p>{tab.commentDescription}</p>
                        ) : null}
                        <Form.Item name="comment">
                          <TextArea
                            rows={4}
                            maxLength={150}
                            disabled={sendDisable}
                          />
                        </Form.Item>
                      </Col>

                      <Col span={4} className="align-end">
                        <div>
                          <Button
                            disabled={sendDisable}
                            className="btn-submit"
                            onClick={() => {
                              onFinish(form.getFieldsValue());
                            }}
                          >
                            Send
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                );
              })}
            </Tabs>
          </Row>
        ) : null}

        <Form.Item name="id" style={{ display: 'none' }}>
          <Input />
        </Form.Item>
      </Form>

      <Popup
        styleBackground="white"
        styleColor="#2A2D51"
        visible={openPopupDelete}
        textCancel="취소"
        textOk="확인"
        title={removeTitle!}
        action_oke={() => {
          removeItem?.();
        }}
        action_cancel={() => setOpenPopupDelete(false)}
      />
    </div>
  );
};

export default CommentTabsComponent;
